// Components==============
import Modal from 'components/UI/Modal/Modal';
import Search from 'components/UI/Search/Search';
import useSearchUsers from 'hooks/useSearchUsers';
import { useTranslate } from 'hooks/useTranslate';
import React, { createContext, useState } from 'react';
import { TranslateType } from 'typings';
import ProjectShareCode from 'pages/Workspace/ProjectDetails/components/GroupManager/ProjectShareCode/ProjectShareCode';
import Button from '../Button/Button';
import Spinner from '../Spinner/Spinner';
import './AddUserModal.module.scss';
import User from './User/User';
// =========================

type AddUserModalContextType = {
  selectedUsers: UserEntity[];
  setSelectedUsers: React.Dispatch<React.SetStateAction<UserEntity[]>>;
  value: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  message: (user: UserEntity) => string;
};

export type ShouldDispatchType = 'initial' | 'pending' | 'dispatch';

export const AddUserModalContext = createContext({} as AddUserModalContextType);

export default function AddUserModal({
  setModalIsOpen,
  title,
  description,
  customTop,
  onSubmit,
  message,
  searchFilter,
  includeSelf,
  includeExtern,
  type,
}: {
  setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  title: TranslateType;
  description?: TranslateType;
  customTop?: React.ReactNode;
  onSubmit: (userIds: number[]) => void;
  message: (user: UserEntity) => string;
  searchFilter?: (user: UserEntity) => boolean;
  includeSelf?: boolean;
  includeExtern?: boolean;
  type?: 'feedback';
}) {
  const translate = useTranslate();

  const [value, setValue] = useState('');
  const [searchResults, setSearchResults] = useState<UserEntity[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserEntity[]>([]);

  const [shouldDispatch, setShouldDispatch] = useState<ShouldDispatchType>(
    'initial'
  );

  useSearchUsers({
    setSearchResults,
    setShouldDispatch,
    shouldDispatch,
    value,
    includeSelf,
    includeExtern,
    searchFilter,
  });

  const hasContent = !!(searchResults.length || selectedUsers.length);

  return (
    <AddUserModalContext.Provider
      value={{
        selectedUsers,
        setSelectedUsers,
        value,
        setValue,
        message,
      }}
    >
      <Modal setModalIsOpen={setModalIsOpen} size="medium">
        <h1 styleName="title">
          {title} <Spinner loading={shouldDispatch === 'pending'} />
        </h1>
        <div styleName="content">
          <div styleName="top">
            {customTop || (
              <>
                <p>{description}</p>
                <div styleName="search">
                  <Search value={value} setValue={setValue} fluid />
                </div>
              </>
            )}
          </div>
          {hasContent ? (
            <div styleName="body">
              {selectedUsers
                .filter(
                  (su) => !searchResults.find((search) => search.id === su.id)
                )
                .map((user) => (
                  <User user={user} key={user.id} />
                ))}
              {searchResults.map((user) => (
                <User user={user} key={user.id} />
              ))}
            </div>
          ) : value && shouldDispatch === 'initial' ? (
            <div styleName="no-results">
              <h2>
                {translate('project.groupswork-popups.no-search-results')}
              </h2>
              <p>
                {translate(
                  'project.groupswork-popups.no-search-results-description'
                )}
              </p>
            </div>
          ) : type === 'feedback' ? null : (
            <div styleName="body">
              <hr styleName="divider " />
              <h2>Externe uitnodigen</h2>
              <p>
                Wil je graag iemand buiten je organisatie uitnodigen, zoals een
                stagebegeleider of vakexpert? Nodig deze persoon uit om een
                extern Groow-account aan te maken via de onderstaande link. Deze
                persoon kan dan meekijken in het proces, feedback geven en
                beoordelen.
              </p>
              <ProjectShareCode />
            </div>
          )}
          <div styleName="button-wrapper">
            <Button color="grey" onClick={() => setModalIsOpen(false)}>
              {translate('general.terms.cancel')}
            </Button>
            <Button
              onClick={() => {
                onSubmit(selectedUsers.map((s) => s.id));
                setModalIsOpen(false);
              }}
              inactive={!selectedUsers.length}
            >
              {translate('project.groupswork-popups.owner-image-popup.invite')}
            </Button>
          </div>
        </div>
      </Modal>
    </AddUserModalContext.Provider>
  );
}
